/**
 * Authored zone layouts (docs/archive/YANYU.md §9.4): a village described as shapes in data
 * (data/layouts/<zone>.ts) and rasterized here into the map's layers, once per zone, with no world
 * seed. Scatter (trees, bamboo, boulders) draws from the layout's own `mulberry32(spec.seed)`.
 *
 * The layout paints, op by op, a kind and a level (metres) on each tile, as
 * `scripts/layouts/yanyu-layout.mjs` (the design sketch the review images were drawn from) does. Then:
 * - natural ground (and anything laid at the ground's level) is graded so that no two neighbours
 *   differ by more than `GRADE`, and it meets the built pads round it unless they are `hard`;
 * - flights of stairs and ramps become `MapFlight`s, with a continuous incline the rules walk on;
 * - the `edge` mask records which tile sides are walls (levels apart by more than MAX_STEP);
 * - kinds become rule tiles (`Tile`) and look surfaces (`Surface`).
 *
 * Pure and deterministic: no randomness but the layout's seed, no time, no DOM.
 */
import { clamp, hash2, lerp, mulberry32, valueNoise } from './rng';
import { BASE_KIND, type BuildingType, type Facing, type MapFlight, type MapProp, type Region, type Structure } from './map';
import type { Side, ZoneId } from './data/zones';
import { SHALLOWS } from './data/shallows';

// ── Spec ────────────────────────────────────────────────────────────────────────────────────────

/** What lies on a tile in a layout (the sketch's kinds). */
export const LAYOUT_KINDS = [
  'grass',
  'sea',
  'sand',
  'rock',
  'forest',
  'bamboo',
  'road',
  'pave',
  'deck',
  'paddy',
  'bund',
  'pond',
  'brook',
  'building',
  'wall',
  'stairs',
  'yard',
  'plot',
  'lotus',
  'redrock',
  'fence',
  'gravel',
  'snow',
  'reeds',
  'boulder',
  'post',
  'banyan',
  /** A compound's courtyard: paving as `pave`, laid in flagstones (the scenery's grey flags or slate). */
  'court',
] as const;
export type LayoutKind = (typeof LAYOUT_KINDS)[number];

/** Tiles [x0, x1) × [y0, y1). */
export type Rect = readonly [x0: number, x1: number, y0: number, y1: number];
export type XY = readonly [x: number, y: number];

/**
 * A level in metres: a number; `'ground'` (the natural ground, graded with it); a straight (or
 * smoothstep) run between two levels along tile columns `i`, rows `j` or the distance `s` (0..1)
 * along a path; a tent rising to `arch[1]` in the middle of `span`; or no lower than `atLeast`.
 */
export type LevelExpr =
  | number
  | 'ground'
  | {
      readonly lerp: readonly [number, number];
      readonly along: 'i' | 'j' | 's';
      /** Tile index range the run spans (i or j); `s` always runs 0..1. */
      readonly span?: readonly [number, number];
      readonly ease?: 'smooth';
      /** `s` is multiplied by this first. */
      readonly scale?: number;
    }
  | { readonly arch: readonly [number, number]; readonly along: 'i' | 'j'; readonly span: readonly [number, number] }
  | { readonly atLeast: number };

/** A band of a disc or an ellipse: tiles under `upTo` (tiles for a disc, the ellipse's own scale). */
export interface Ring {
  readonly upTo?: number;
  readonly kind: LayoutKind;
  readonly level?: LevelExpr;
  /** An opening in the band, round the angle `at` (radians, atan2 of y, x) ± `half`. */
  readonly gap?: { readonly at: readonly number[]; readonly half: number; readonly kind: LayoutKind; readonly level?: LevelExpr };
  /** Skipped where the tile is (still) one of these. */
  readonly skip?: readonly LayoutKind[];
}

export type Shape =
  | { readonly rect: Rect }
  /** A rectangle with its corners cut where (in from x + in from y) ≤ `cut`. */
  | { readonly rect: Rect; readonly cut: number }
  | { readonly disc: readonly [cx: number, cy: number, r: number] }
  | { readonly ellipse: readonly [cx: number, cy: number, rx: number, ry: number] }
  /** Tiles within `width / 2` of a polyline (at least 1.05 wide). */
  | { readonly path: readonly XY[]; readonly width: number }
  /** The one-tile ring round a rectangle (a retaining wall round a terrace). */
  | { readonly around: Rect };

export interface PaintOp {
  readonly name?: string;
  readonly shape: Shape;
  readonly kind?: LayoutKind;
  readonly level?: LevelExpr;
  /** Rings by distance, for a disc or an ellipse (in order, the first that holds). */
  readonly rings?: readonly Ring[];
  /** Painted only where the tile is one of these / none of these. */
  readonly only?: readonly LayoutKind[];
  readonly skip?: readonly LayoutKind[];
  /** Painted only inside this rectangle too. */
  readonly clip?: Rect;
  /** Along a path: rock is left alone before this far along it (0..1). */
  readonly keepRockBefore?: number;
  /** Rock deeper than this many tiles inside the western range is left alone. */
  readonly keepRange?: number;
  /** Path tiles whose distance along it falls in these ranges are stairs (to look at). */
  readonly stairsAlong?: readonly (readonly [number, number])[];
  /**
   * Hard: natural ground is not graded up to it, so where they differ its edges stay walls (a
   * raised yard, a deck). Soft (the default): the ground round it meets it.
   */
  readonly hard?: boolean;
  /** Its tiles form one flight (the path's own incline): walked as a continuous slope. */
  readonly flight?: 'stairs' | 'ramp';
  /** A disc or an ellipse: only from this far out (its own scale). */
  readonly minR?: number;
  /** Only where the tile's hash is above this (a speckle: lotus among open water). */
  readonly speckle?: number;
}

/**
 * Fields: plots (rice paddies, vegetable beds) in bands stepping down by rows, with bunds (paths,
 * `bund` higher) every so many columns and rows of each field and on the given rows.
 */
export interface FieldsOp {
  readonly name?: string;
  readonly fields: readonly Rect[];
  readonly plot: 'paddy' | 'plot';
  /** [rows before this, level], in order. */
  readonly bands: readonly (readonly [number, number])[];
  /** A bund every [columns, rows] (0: none that way). */
  readonly every: readonly [number, number];
  readonly bundRows?: readonly number[];
  readonly bund: number;
  readonly skip?: readonly LayoutKind[];
}

/** A straight flight of stairs (or a ramp) over a rectangle: from `bottom` at its foot to `top`. */
export interface FlightOp {
  readonly name: string;
  readonly flight: 'stairs' | 'ramp';
  readonly rect: Rect;
  /** The side of the foot (the low end). */
  readonly down: Facing;
  readonly bottom: number;
  readonly top: number;
  readonly steps?: number;
}

/** Trees, bamboo and boulders scattered over grass, drawn from the layout's seed in order. */
export interface ScatterOp {
  readonly name?: string;
  readonly scatter: {
    readonly rect: Rect;
    /** Tried in order on each grass tile; a rule's dice is thrown only when its conditions hold. */
    readonly rules: readonly {
      /** Value noise at (i / sx + ox, j / sy + oy) must be above `above`. */
      readonly noise?: readonly [sx: number, ox: number, sy: number, oy: number, above: number];
      /** Rows [j0, j1) only. */
      readonly rows?: readonly [number, number];
      readonly p: number;
      readonly kind: LayoutKind;
    }[];
    /** A clearing left untouched: tiles whose corner is within `r` of the point. */
    readonly clear?: readonly [x: number, y: number, r: number];
  };
}

/** A fence (or palisade) along a polyline, with gaps for gates. */
export interface FenceOp {
  readonly name?: string;
  readonly fence: readonly XY[];
  readonly gaps?: readonly Rect[];
  /** Its top, above the ground (metres). */
  readonly height: number;
}

export type LayoutOp = PaintOp | FlightOp | ScatterOp | FenceOp | FieldsOp;

/** How a building looks and stands; its footprint is solid except for open kinds (see `OPEN_KINDS`). */
export interface LayoutBuilding {
  readonly id?: string;
  readonly name: string;
  readonly kind: BuildingType;
  /** Footprint [x0, x1) × [y0, y1). */
  readonly x0: number;
  readonly x1: number;
  readonly y0: number;
  readonly y1: number;
  /** The tile the door is in, and the side it opens to. */
  readonly door?: readonly [i: number, j: number, side: Facing];
  /** Floor level (metres); the ground at its middle if left out. */
  readonly base?: number;
  /** Eaves and the roof's rise above them (metres). */
  readonly eave?: number;
  readonly rise?: number;
  readonly roof?: 'hip' | 'pyramid' | 'tiers' | 'thatch' | 'awning' | 'tent' | 'none' | 'gable';
  /**
   * Yaw (degrees, lane KIT-EXT): the footprint turned about its middle, +x toward +y (map.ts
   * `Structure.rot`); its tiles are those whose middles fall inside. No door on a turned building yet.
   */
  readonly rot?: number;
  /** A pagoda's or a watchtower's eaves stood on (map.ts `Structure.ledges`): a qinggong climb. */
  readonly ledges?: boolean;
  readonly color?: string;
  /** The district it belongs to (docs/archive/YANYU.md §3). */
  readonly district?: number;
  /** Gates and the drum tower: the road runs under them along this axis. */
  readonly axis?: 'x' | 'y';
  /**
   * Open kinds (gates, a paifang, a moon gate, the drum tower): the tiles [from, to) along the
   * footprint's long side (x, or y for a footprint taller than wide) the road runs through, left
   * open; the rest of the footprint is solid (posts and wings).
   */
  readonly passage?: readonly [number, number];
  /** Tiles of the footprint left open inside it (the guard post's cell). */
  readonly cell?: Rect;
}

/** The ground the village sits on, before anything is built (`scripts/layouts/yanyu-layout.mjs` `ground`). */
export interface TerrainSpec {
  /** The coast: piecewise y by x; the sea lies north of it. */
  readonly coast: readonly XY[];
  /**
   * The ranges' feet. West: x of the foot by row bands [until y, base, noise u, noise scale,
   * noise amp, (rise from y, rate, max)]; east: x = base + noise; south: y = base + noise, with a
   * gap for the road.
   */
  readonly west: readonly (readonly number[])[];
  readonly east: readonly [base: number, u: number, scale: number, amp: number];
  readonly south: readonly [base: number, v: number, scale: number, amp: number, gapX: number, gapHalf: number];
  /** Rock: ground at the foot + lift + min(max, depth·rise) ± noise, in ledges of `step`. */
  readonly rock: { readonly lift: number; readonly rise: number; readonly max: number; readonly noise: number; readonly scale: number; readonly step: number };
  /** Rolling ground by row: [y, level, ease into it ('smooth' or straight)]. */
  readonly rows: readonly (readonly [number, number, ('smooth' | 'line')?])[];
  /** Extra rise by column: [x, rate, side (−1: west of it, 1: east of it), from y, to y]. */
  readonly slopes: readonly (readonly [x: number, rate: number, side: -1 | 1, y0: number, y1: number])[];
  /** A plateau set to `level` ± noise inside a rectangle. */
  readonly plateaus: readonly { readonly rect: Rect; readonly level: number; readonly noise: number; readonly scale: number }[];
  /** Gaussian rises added before the ridges, and hills added after them: [x, y, sigma, amp]. */
  readonly rises: readonly (readonly [number, number, number, number])[];
  readonly hills: readonly (readonly [number, number, number, number])[];
  /** A ridge (the max of a chain of gaussians from `from` to `to`, amp `amp[0]` → `amp[1]`). */
  readonly ridges: readonly { readonly from: XY; readonly to: XY; readonly sigma: number; readonly amp: readonly [number, number]; readonly n: number }[];
  /** Undulation: (noise(x/scale, y/scale) − 0.5) × amp. */
  readonly noise: readonly [scale: number, amp: number];
  /** Land near the sea: no higher than base + d·rise for d < reach; sand under `sand`, except in `keep`. */
  readonly beach: { readonly reach: number; readonly base: number; readonly rise: number; readonly sand: number; readonly keep: readonly Rect[] };
}

export interface LayoutSpec {
  readonly id: string;
  readonly zone: ZoneId;
  readonly w: number;
  readonly h: number;
  readonly seed: number;
  readonly terrain: TerrainSpec;
  readonly ops: readonly LayoutOp[];
  readonly buildings: readonly LayoutBuilding[];
  /** Boats on the water (scenery): middle, length, along which axis, kind. */
  readonly boats: readonly { readonly x: number; readonly y: number; readonly len: number; readonly axis: 'x' | 'y'; readonly kind: 'ferry' | 'boat' | 'skiff'; readonly name?: string }[];
  /** Posts of the pole forest (梅花桩): standing tops (metres). */
  readonly posts: readonly { readonly x: number; readonly y: number; readonly h: number }[];
  /** Landmark trees and props (scenery, for the renderers). */
  readonly props: readonly MapProp[];
  readonly landmarks: Readonly<Record<string, XY>>;
  /** The map's own points. */
  readonly points: {
    readonly plaza: XY;
    readonly spawn: XY;
    readonly elder: XY;
    readonly jail: XY;
    readonly duel: XY;
    readonly arena: XY;
    readonly lair: XY;
  };
  readonly regions: Readonly<Record<'wolves' | 'bandits' | 'west' | 'east' | 'graves', Region>>;
  /** Warp circles where the layout puts them (one per neighbouring zone, see `linksOf`). */
  readonly portals: readonly { readonly to: ZoneId; readonly side: Side; readonly at: XY }[];
  /** Warps kept for zones still to come: placed, not active. */
  readonly reserved: readonly { readonly name: string; readonly side: Side; readonly at: XY }[];
  /** The safe village: inside this polygon no player is killed (PK off). */
  readonly safe: readonly XY[];
  /** Tiles kept clear of scatter round every landmark, point and door front (Chebyshev radius). */
  readonly keepClear: number;
}

// ── What it builds ──────────────────────────────────────────────────────────────────────────────

/** A layout raster before it becomes rule tiles (map.ts turns it into a `GameMap`). */
export interface BuiltLayout {
  readonly spec: LayoutSpec;
  readonly w: number;
  readonly h: number;
  /** Index into LAYOUT_KINDS. */
  readonly kind: Uint8Array;
  /** Metres (centre of the tile: a flight's incline at its middle). */
  readonly level: Float32Array;
  /** 0 natural ground, 1 laid soft (the ground meets it), 2 hard (its edges stay walls). */
  readonly made: Uint8Array;
  /** Sea depth class 1..3 (0 on land and inland water). */
  readonly deep: Uint8Array;
  readonly flights: readonly MapFlight[];
  /** 1 + index into `flights`, else 0. */
  readonly flight: Uint16Array;
  readonly structures: readonly Structure[];
  /** Per tile: 1 + index of the structure, or 0. */
  readonly owner: Uint16Array;
  readonly safe: Uint8Array;
  readonly props: readonly MapProp[];
}

/** Most neighbouring tiles of natural ground may differ (metres), under MAX_STEP. */
export const GRADE = 0.3;

/** Building colours, by `Structure.variant`. */
export const COLORS = ['blue', 'red', 'white', 'green'] as const;

const K = Object.fromEntries(LAYOUT_KINDS.map((k, n) => [k, n])) as Record<LayoutKind, number>;
const WATER_KINDS = new Set<number>([K.sea, K.pond, K.brook, K.lotus]);
/** Kinds whose own footprint is mostly open: only the corner (or end) posts are solid. */
export const OPEN_KINDS: ReadonlySet<BuildingType> = new Set<BuildingType>([
  'gate',
  'paifang',
  'shed',
  'pavilion',
  'drumtower',
  'kiosk',
  'moongate',
]);

const smooth = (t: number) => (t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t));
const gauss = (x: number, y: number, cx: number, cy: number, s: number, amp: number) =>
  amp * Math.exp(-((x - cx) ** 2 + (y - cy) ** 2) / (2 * s * s));

function piecewise(points: readonly XY[], x: number): number {
  for (let k = 1; k < points.length; k++) {
    const [x0, y0] = points[k - 1]!;
    const [x1, y1] = points[k]!;
    if (x <= x1) return lerp(y0, y1, (x - x0) / (x1 - x0));
  }
  return points[points.length - 1]![1];
}

/** The terrain's functions (pure, from the spec). */
function terrainOf(t: TerrainSpec) {
  const coast = (x: number) => piecewise(t.coast, x);
  const westFoot = (y: number): number => {
    for (const band of t.west) {
      const [until, base, u = 0, scale = 1, amp = 0, riseFrom = Infinity, rate = 0, max = 0] = band;
      if (y < until!) {
        let f = base! + (amp ? valueNoise(u, y / scale) * amp : 0);
        if (y > riseFrom) f += Math.min(max, (y - riseFrom) * rate);
        return f;
      }
    }
    return 0;
  };
  const eastFoot = (y: number) => t.east[0] + valueNoise(t.east[1], y / t.east[2]) * t.east[3];
  const southFoot = (x: number) => t.south[0] + valueNoise(x / t.south[2], t.south[1]) * t.south[3];
  const ground = (x: number, y: number): number => {
    const rows = t.rows;
    let h = rows[0]![1];
    for (let k = 1; k < rows.length; k++) {
      const [y0, h0] = rows[k - 1]!;
      const [y1, h1, ease] = rows[k]!;
      if (y < y1 || k === rows.length - 1) {
        const f = (y - y0) / (y1 - y0);
        h = y < y0 ? h0 : lerp(h0, h1, ease === 'smooth' ? smooth(f) : clamp(f, 0, Infinity));
        break;
      }
    }
    for (const [sx, rate, side, y0, y1] of t.slopes)
      if (y > y0 && y <= y1 && (side < 0 ? x < sx : x > sx)) h += Math.abs(x - sx) * rate;
    for (const p of t.plateaus)
      if (x > p.rect[0] && x <= p.rect[1] && y > p.rect[2] && y <= p.rect[3])
        h = p.level + valueNoise(x / p.scale, y / p.scale) * p.noise;
    for (const [cx, cy, s, amp] of t.rises) h += gauss(x, y, cx, cy, s, amp);
    for (const r of t.ridges)
      for (let k = 0; k <= r.n; k++) {
        const f = k / r.n;
        h = Math.max(h, gauss(x, y, lerp(r.from[0], r.to[0], f), lerp(r.from[1], r.to[1], f), r.sigma, lerp(r.amp[0], r.amp[1], f)));
      }
    for (const [cx, cy, s, amp] of t.hills) h += gauss(x, y, cx, cy, s, amp);
    h += (valueNoise(x / t.noise[0], y / t.noise[0]) - 0.5) * t.noise[1];
    return h;
  };
  return { coast, westFoot, eastFoot, southFoot, ground };
}

/** Distance from a point to a segment, and how far along it (0..1). */
function segDist(px: number, py: number, ax: number, ay: number, bx: number, by: number): [number, number] {
  const dx = bx - ax;
  const dy = by - ay;
  const t = clamp(((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy || 1), 0, 1);
  return [Math.hypot(px - ax - t * dx, py - ay - t * dy), t];
}

/** Nearest distance to a polyline and the distance along it (0..1) at that nearest point. */
export function alongPath(pts: readonly XY[], x: number, y: number): [d: number, s: number] {
  let L = 0;
  const lens = [0];
  for (let k = 1; k < pts.length; k++) {
    L += Math.hypot(pts[k]![0] - pts[k - 1]![0], pts[k]![1] - pts[k - 1]![1]);
    lens.push(L);
  }
  L ||= 1;
  let best = Infinity;
  let s = 0;
  for (let k = 1; k < pts.length; k++) {
    const [d, t] = segDist(x, y, pts[k - 1]![0], pts[k - 1]![1], pts[k]![0], pts[k]![1]);
    if (d < best) {
      best = d;
      s = (lens[k - 1]! + t * (lens[k]! - lens[k - 1]!)) / L;
    }
  }
  return [best, s];
}

/** Whether a point lies inside a polygon (even-odd). */
export function inPolygon(poly: readonly XY[], x: number, y: number): boolean {
  let inside = false;
  for (let a = 0, b = poly.length - 1; a < poly.length; b = a++) {
    const [xa, ya] = poly[a]!;
    const [xb, yb] = poly[b]!;
    if (ya > y !== yb > y && x < ((xb - xa) * (y - ya)) / (yb - ya) + xa) inside = !inside;
  }
  return inside;
}

// ── The builder ─────────────────────────────────────────────────────────────────────────────────

/** Rasterizes a layout. Pure: the same spec always gives the same layers. */
export function buildAuthored(spec: LayoutSpec): BuiltLayout {
  const { w: W, h: H } = spec;
  const N = W * H;
  const T = terrainOf(spec.terrain);
  const rnd = mulberry32(spec.seed);
  const kind = new Uint8Array(N);
  const level = new Float32Array(N);
  const made = new Uint8Array(N);
  const deep = new Uint8Array(N);
  /** Tiles whose level follows the natural ground (graded with it). */
  const natural = new Uint8Array(N);
  const at = (i: number, j: number) => j * W + i;
  const inMap = (i: number, j: number) => i >= 0 && j >= 0 && i < W && j < H;
  const get = (i: number, j: number) => (inMap(i, j) ? kind[at(i, j)]! : K.rock);
  const flights: MapFlight[] = [];
  const flight = new Uint16Array(N);

  const evalLevel = (e: LevelExpr | undefined, i: number, j: number, s: number): number | 'ground' | null => {
    if (e === undefined) return null;
    if (typeof e === 'number') return e;
    if (e === 'ground') return 'ground';
    const cur = level[at(i, j)]!;
    if ('atLeast' in e) return Math.max(cur, e.atLeast);
    if ('arch' in e) {
      const c = e.along === 'i' ? i + 0.5 : j + 0.5;
      const f = clamp((c - e.span[0]) / (e.span[1] - e.span[0]), 0, 1);
      return lerp(e.arch[0], e.arch[1], 1 - Math.abs(2 * f - 1));
    }
    let f: number;
    if (e.along === 's') f = s * (e.scale ?? 1);
    else {
      const c = e.along === 'i' ? i : j;
      const [c0, c1] = e.span ?? [0, 1];
      f = (c - c0) / (c1 - c0);
    }
    f = e.ease === 'smooth' ? smooth(f) : clamp(f, 0, 1);
    return lerp(e.lerp[0], e.lerp[1], f);
  };
  const paint = (i: number, j: number, k: LayoutKind | undefined, lv: number | 'ground' | null, hard: boolean) => {
    if (!inMap(i, j)) return;
    const n = at(i, j);
    if (k !== undefined) kind[n] = K[k];
    if (lv === 'ground') {
      natural[n] = 1;
      made[n] = 1;
      level[n] = T.ground(i + 0.5, j + 0.5);
    } else if (lv !== null) {
      natural[n] = 0;
      made[n] = hard ? 2 : 1;
      level[n] = lv;
    }
    flight[n] = 0;
  };

  // 1. Sea, mountains and the rolling ground.
  const tr = spec.terrain;
  for (let j = 0; j < H; j++)
    for (let i = 0; i < W; i++) {
      const x = i + 0.5;
      const y = j + 0.5;
      const n = at(i, j);
      const c = T.coast(x);
      if (y < c) {
        kind[n] = K.sea;
        level[n] = 0;
        const d = c - y;
        deep[n] = d < 2.5 ? 1 : d < 6 ? 2 : 3;
        continue;
      }
      const wf = T.westFoot(y);
      const inWest = x < wf;
      const inEast = x > T.eastFoot(y);
      const inSouth = y > T.southFoot(x) && Math.abs(x - tr.south[4]) > tr.south[5];
      if (inWest || inEast || inSouth) {
        const depth = inWest ? wf - x : inEast ? x - T.eastFoot(y) : y - T.southFoot(x);
        const base = T.ground(Math.max(x, wf), y);
        const R = tr.rock;
        const hgt = base + R.lift + Math.min(R.max, depth * R.rise) + (valueNoise(x / R.scale, y / R.scale) - 0.5) * R.noise;
        kind[n] = K.rock;
        level[n] = Math.round(hgt / R.step) * R.step;
        continue;
      }
      kind[n] = K.grass;
      level[n] = T.ground(x, y);
      natural[n] = 1;
    }

  // 2. The ops, in order.
  const forRect = (r: Rect, fn: (i: number, j: number) => void) => {
    for (let j = Math.floor(r[2]); j < Math.ceil(r[3]); j++)
      for (let i = Math.floor(r[0]); i < Math.ceil(r[1]); i++) if (inMap(i, j)) fn(i, j);
  };
  const beach = () => {
    const b = tr.beach;
    for (let j = 0; j < H; j++)
      for (let i = 0; i < W; i++) {
        if (get(i, j) !== K.grass) continue;
        const d = j + 0.5 - T.coast(i + 0.5);
        if (d < b.reach) {
          const n = at(i, j);
          level[n] = Math.min(level[n]!, b.base + d * b.rise);
          if (d < b.sand && !b.keep.some((r) => i >= r[0] && i < r[1] && j >= r[2] && j < r[3])) kind[n] = K.sand;
        }
      }
  };
  // The beach is part of the terrain: before the first op.
  beach();

  for (const op of spec.ops) {
    if ('flight' in op && 'rect' in op) {
      const f = op as FlightOp;
      flights.push({
        kind: f.flight,
        name: f.name,
        rect: f.rect,
        down: f.down,
        bottom: f.bottom,
        top: f.top,
        steps: f.steps ?? (f.flight === 'stairs' ? Math.max(2, Math.round((f.top - f.bottom) / 0.18)) : 0),
      });
      const fl = flights[flights.length - 1]!;
      forRect(f.rect, (i, j) => {
        paint(i, j, f.flight === 'stairs' ? 'stairs' : 'road', flightLevel(fl, i + 0.5, j + 0.5), false);
        flight[at(i, j)] = flights.length;
      });
      continue;
    }
    if ('scatter' in op) {
      const sc = op.scatter;
      forRect(sc.rect, (i, j) => {
        if (get(i, j) !== K.grass) return;
        if (sc.clear && Math.hypot(i - sc.clear[0], j - sc.clear[1]) < sc.clear[2]) return;
        for (const r of sc.rules) {
          if (r.rows && (j < r.rows[0] || j >= r.rows[1])) continue;
          if (r.noise && !(valueNoise(i / r.noise[0] + r.noise[1], j / r.noise[2] + r.noise[3]) > r.noise[4])) continue;
          if (rnd() < r.p) {
            kind[at(i, j)] = K[r.kind];
            return;
          }
        }
      });
      continue;
    }
    if ('fields' in op) {
      const bandOf = (j: number) => op.bands.find(([until]) => j < until)![1];
      for (const r of op.fields)
        forRect(r, (i, j) => {
          const k = get(i, j);
          if (op.skip?.some((o) => K[o] === k)) return;
          const [ci, cj] = op.every;
          const bund =
            (ci > 0 && (i - r[0]) % ci === ci - 1) ||
            (cj > 0 && (j - r[2]) % cj === cj - 1) ||
            !!op.bundRows?.includes(j);
          const n = at(i, j);
          kind[n] = bund ? K.bund : K[op.plot];
          level[n] = bandOf(j) + (bund ? op.bund : 0);
          natural[n] = 0;
          made[n] = 1;
          flight[n] = 0;
        });
      continue;
    }
    if ('fence' in op) {
      const pts = op.fence;
      const xs = pts.map((p) => p[0]);
      const ys = pts.map((p) => p[1]);
      forRect([Math.min(...xs) - 1, Math.max(...xs) + 1, Math.min(...ys) - 1, Math.max(...ys) + 1], (i, j) => {
        const [d] = alongPath(pts, i + 0.5, j + 0.5);
        if (d >= 1.05 / 2) return;
        if (op.gaps?.some((g) => i >= g[0] && i < g[1] && j >= g[2] && j < g[3])) return;
        const k = get(i, j);
        if (k === K.road || k === K.rock || k === K.wall || WATER_KINDS.has(k) || k === K.paddy) return;
        const n = at(i, j);
        const g = level[n]!;
        kind[n] = K.fence;
        level[n] = g + op.height;
        natural[n] = 0;
        made[n] = 2;
        flight[n] = 0;
      });
      continue;
    }
    const p = op as PaintOp;
    const sh = p.shape;
    const allowed = (i: number, j: number) => {
      const k = get(i, j);
      if (p.only && !p.only.some((o) => K[o] === k)) return false;
      if (p.skip?.some((o) => K[o] === k)) return false;
      if (p.clip && !(i >= p.clip[0] && i < p.clip[1] && j >= p.clip[2] && j < p.clip[3])) return false;
      if (p.keepRange !== undefined && k === K.rock && i + 0.5 < T.westFoot(j + 0.5) - p.keepRange) return false;
      if (p.speckle !== undefined && !(hash2(i, j) > p.speckle)) return false;
      return true;
    };
    const hard = !!p.hard;
    const ringPaint = (i: number, j: number, d: number, ang: number) => {
      for (const r of p.rings!) {
        if (r.upTo !== undefined && !(d < r.upTo)) continue;
        if (r.skip?.some((o) => K[o] === get(i, j))) return;
        const gap = r.gap && r.gap.at.some((a) => Math.abs(((ang - a + 3 * Math.PI) % (2 * Math.PI)) - Math.PI) < r.gap!.half);
        if (gap) paint(i, j, r.gap!.kind, evalLevel(r.gap!.level, i, j, 0), hard);
        else paint(i, j, r.kind, evalLevel(r.level, i, j, 0), hard);
        return;
      }
    };
    if ('around' in sh) {
      const [x0, x1, y0, y1] = sh.around;
      forRect([x0 - 1, x1 + 1, y0 - 1, y1 + 1], (i, j) => {
        if (i >= x0 && i < x1 && j >= y0 && j < y1) return;
        if (!allowed(i, j)) return;
        paint(i, j, p.kind, evalLevel(p.level, i, j, 0), true);
      });
    } else if ('rect' in sh) {
      const [x0, x1, y0, y1] = sh.rect;
      const cut = 'cut' in sh ? sh.cut : -1;
      forRect(sh.rect, (i, j) => {
        if (cut >= 0) {
          const cx = Math.min(i + 0.5 - x0, x1 - i - 0.5);
          const cy = Math.min(j + 0.5 - y0, y1 - j - 0.5);
          if (cx + cy <= cut) return;
        }
        if (!allowed(i, j)) return;
        paint(i, j, p.kind, evalLevel(p.level, i, j, 0), hard);
      });
    } else if ('disc' in sh) {
      const [cx, cy, r] = sh.disc;
      forRect([cx - r - 1, cx + r + 1, cy - r - 1, cy + r + 1], (i, j) => {
        const d = Math.hypot(i + 0.5 - cx, j + 0.5 - cy);
        if (!(d < r) || d < (p.minR ?? 0) || !allowed(i, j)) return;
        const ang = Math.atan2(j + 0.5 - cy, i + 0.5 - cx);
        if (p.rings) ringPaint(i, j, d, ang);
        else paint(i, j, p.kind, evalLevel(p.level, i, j, 0), hard);
      });
    } else if ('ellipse' in sh) {
      const [cx, cy, rx, ry] = sh.ellipse;
      const reach = Math.max(...(p.rings ?? [{ upTo: 1 }]).map((r) => r.upTo ?? 1));
      forRect([cx - rx * reach - 1, cx + rx * reach + 1, cy - ry * reach - 1, cy + ry * reach + 1], (i, j) => {
        const e = Math.hypot((i + 0.5 - cx) / rx, (j + 0.5 - cy) / ry);
        if (!(e < reach) || e < (p.minR ?? 0) || !allowed(i, j)) return;
        const ang = Math.atan2(j + 0.5 - cy, i + 0.5 - cx);
        if (p.rings) ringPaint(i, j, e, ang);
        else paint(i, j, p.kind, evalLevel(p.level, i, j, 0), hard);
      });
    } else {
      const pts = sh.path;
      const width = Math.max(sh.width, 1.05);
      const xs = pts.map((q) => q[0]);
      const ys = pts.map((q) => q[1]);
      let fl: MapFlight | null = null;
      if (p.flight) {
        const lv = p.level;
        const [a, b] = typeof lv === 'object' && 'lerp' in lv ? lv.lerp : [0, 0];
        flights.push({
          kind: p.flight,
          name: p.name ?? 'path',
          rect: [Math.floor(Math.min(...xs) - width), Math.ceil(Math.max(...xs) + width), Math.floor(Math.min(...ys) - width), Math.ceil(Math.max(...ys) + width)],
          down: 'n',
          bottom: Math.min(a, b),
          top: Math.max(a, b),
          steps: 0,
          path: { pts, width, from: a, to: b },
        });
        fl = flights[flights.length - 1]!;
      }
      forRect([Math.min(...xs) - width, Math.max(...xs) + width, Math.min(...ys) - width, Math.max(...ys) + width], (i, j) => {
        const [d, s] = alongPath(pts, i + 0.5, j + 0.5);
        if (!(d < width / 2)) return;
        if (p.keepRockBefore !== undefined && s < p.keepRockBefore && get(i, j) === K.rock) return;
        if (!allowed(i, j)) return;
        const stairs = p.stairsAlong?.some(([s0, s1]) => s >= s0 && s < s1);
        paint(i, j, stairs ? 'stairs' : p.kind, fl ? flightLevel(fl, i + 0.5, j + 0.5) : evalLevel(p.level, i, j, s), hard);
        if (fl) flight[at(i, j)] = flights.length;
      });
    }
  }

  // 3. Buildings: footprints solid (open kinds only at their corner posts), doors kept clear.
  const structures: Structure[] = [];
  const owner = new Uint16Array(N);
  /**
   * A building's tiles, each with the tile of its own (unturned) footprint it stands for: square
   * footprints tile for tile; a turned one (`rot`) the tiles whose middles fall inside it.
   */
  const forFootprint = (b: LayoutBuilding, fn: (i: number, j: number, n: number) => void) => {
    if (!b.rot) return forRect([b.x0, b.x1, b.y0, b.y1], (i, j) => fn(i, j, at(i, j)));
    forRect(footBounds(b), (i, j) => {
      const [u, v] = footLocal(b, i + 0.5, j + 0.5);
      if (u >= b.x0 && u < b.x1 && v >= b.y0 && v < b.y1) fn(Math.floor(u), Math.floor(v), at(i, j));
    });
  };
  /** Buildings whose floor is the ground round them, settled once the ground is graded. */
  const settle: { k: number; b: LayoutBuilding }[] = [];
  for (const b of spec.buildings) {
    if (b.base === undefined) settle.push({ k: structures.length, b });
    const base = b.base ?? NaN;
    const wTiles = b.x1 - b.x0;
    const hTiles = b.y1 - b.y0;
    const st: Structure = {
      kind: BASE_KIND[b.kind],
      type: b.kind,
      i: b.x0,
      j: b.y0,
      w: wTiles,
      h: hTiles,
      variant: Math.max(0, (COLORS as readonly string[]).indexOf(b.color ?? 'blue')),
      name: b.name,
      base,
      ...(b.eave !== undefined ? { eave: b.eave } : {}),
      ...(b.rise !== undefined ? { rise: b.rise } : {}),
      ...(b.roof ? { roof: b.roof } : {}),
      ...(b.rot ? { rot: b.rot } : {}),
      ...(b.ledges ? { ledges: true } : {}),
      ...(b.axis ? { axis: b.axis } : wTiles === 1 || hTiles === 1 ? { axis: wTiles === 1 ? 'y' : 'x' } : {}),
      ...(b.id ? { id: b.id } : {}),
      ...(b.door ? { door: { i: b.door[0], j: b.door[1], facing: b.door[2] } } : {}),
      ...(b.district !== undefined ? { district: b.district } : {}),
      // The passage counted from the footprint's start, so it moves with the structure (a region stamp).
      ...(b.passage ? { passage: [b.passage[0] - (hTiles > wTiles ? b.y0 : b.x0), b.passage[1] - (hTiles > wTiles ? b.y0 : b.x0)] as const } : {}),
    };
    structures.push(st);
    const open = OPEN_KINDS.has(b.kind);
    forFootprint(b, (i, j, n) => {
      const corner = (i === b.x0 || i === b.x1 - 1) && (j === b.y0 || j === b.y1 - 1);
      const t = hTiles > wTiles ? j : i;
      const road = !!b.passage && t >= b.passage[0] && t < b.passage[1];
      const cell = !!b.cell && i >= b.cell[0] && i < b.cell[1] && j >= b.cell[2] && j < b.cell[3];
      const solid = cell ? false : !open || (b.passage ? !road : corner);
      if (cell) {
        kind[n] = K.yard;
        level[n] = base;
        natural[n] = 0;
        made[n] = 2;
        flight[n] = 0;
      } else if (solid) {
        kind[n] = K.building;
        level[n] = base;
        natural[n] = 0;
        made[n] = 2;
        flight[n] = 0;
        owner[n] = structures.length;
      }
    });
  }
  // Boats moored on the water (scenery, not solid).
  for (const bt of spec.boats) {
    const half = Math.floor(bt.len / 2);
    structures.push({
      kind: 'boat',
      i: Math.round(bt.x) - half,
      j: Math.round(bt.y) - half,
      w: bt.len,
      h: bt.len,
      axis: bt.axis,
      variant: bt.kind === 'ferry' ? 0 : bt.kind === 'boat' ? 1 : 2,
      ...(bt.name ? { name: bt.name } : {}),
    });
  }
  // The pole forest: posts to leap between (solid, their tops stood on).
  for (const p of spec.posts) {
    const n = at(Math.floor(p.x), Math.floor(p.y));
    kind[n] = K.post;
    level[n] = p.h;
    natural[n] = 0;
    made[n] = 2;
  }

  // 4. Clear scatter off every landmark, point and door front.
  const clearAt = (x: number, y: number) => {
    const r = spec.keepClear;
    for (let j = Math.floor(y) - r; j <= Math.floor(y) + r; j++)
      for (let i = Math.floor(x) - r; i <= Math.floor(x) + r; i++) {
        if (!inMap(i, j)) continue;
        const k = kind[at(i, j)]!;
        if (k === K.forest || k === K.bamboo || k === K.boulder) kind[at(i, j)] = K.grass;
      }
  };
  for (const [x, y] of Object.values(spec.landmarks)) clearAt(x, y);
  for (const [x, y] of Object.values(spec.points)) clearAt(x, y);
  for (const p of [...spec.portals, ...spec.reserved]) clearAt(p.at[0], p.at[1]);
  for (const st of structures)
    if (st.door) {
      const [di, dj] = { n: [0, -1], e: [1, 0], s: [0, 1], w: [-1, 0] }[st.door.facing];
      clearAt(st.door.i + di + 0.5, st.door.j + dj + 0.5);
    }

  // 5. Natural ground graded: neighbours within GRADE of each other, and of the soft pads round them.
  const land = (n: number) => {
    const k = kind[n]!;
    return !WATER_KINDS.has(k) && k !== K.rock && k !== K.wall && k !== K.building && k !== K.fence && k !== K.post && k !== K.redrock;
  };
  const pairs: number[] = [];
  for (let j = 0; j < H; j++)
    for (let i = 0; i < W; i++) {
      const a = at(i, j);
      for (const b of [i + 1 < W ? a + 1 : -1, j + 1 < H ? a + W : -1]) {
        if (b < 0 || !land(a) || !land(b)) continue;
        if (!natural[a] && !natural[b]) continue;
        // A hard pad keeps its walls; a flight is met only at its ends (left to the authoring).
        if ((!natural[a] && (made[a] === 2 || flight[a])) || (!natural[b] && (made[b] === 2 || flight[b]))) continue;
        pairs.push(a, b);
      }
    }
  for (let pass = 0; pass < 600; pass++) {
    let moved = false;
    for (let k = 0; k < pairs.length; k += 2) {
      const a = pairs[k]!;
      const b = pairs[k + 1]!;
      const d = level[a]! - level[b]!;
      if (Math.abs(d) <= GRADE + 1e-6) continue;
      moved = true;
      const [hi, lo] = d > 0 ? [a, b] : [b, a];
      if (!natural[hi]) level[lo] = level[hi]! - GRADE;
      else if (!natural[lo]) level[hi] = level[lo]! + GRADE;
      else {
        const mid = (level[hi]! + level[lo]!) / 2;
        level[hi] = mid + GRADE / 2;
        level[lo] = mid - GRADE / 2;
      }
    }
    if (!moved) break;
  }
  // Buildings without a set floor stand at the highest ground round their footprint.
  for (const { k, b } of settle) {
    let top = -Infinity;
    const [bx0, bx1, by0, by1] = footBounds(b);
    for (let j = by0 - 1; j <= by1; j++)
      for (let i = bx0 - 1; i <= bx1; i++) {
        if (!inMap(i, j) || (b.rot ? owner[at(i, j)] === k + 1 : i >= b.x0 && i < b.x1 && j >= b.y0 && j < b.y1)) continue;
        const n = at(i, j);
        if (land(n) && !flight[n]) top = Math.max(top, level[n]!);
      }
    const base = Number.isFinite(top) ? Math.round(top * 20) / 20 : 0;
    structures[k] = { ...structures[k]!, base };
    forRect(footBounds(b), (i, j) => {
      if (owner[at(i, j)] === k + 1 || kind[at(i, j)] === K.yard) level[at(i, j)] = base;
    });
  }

  // Inland water lies under its banks: a pond's surface a little under its lowest bank, a brook's
  // under the banks of each stretch (the ground round it was graded after it was laid).
  const pondish = (k: number) => k === K.pond || k === K.lotus;
  const bank = (n: number) => {
    let low = Infinity;
    const i = n % W;
    const j = (n / W) | 0;
    for (const [a, b] of [
      [i + 1, j],
      [i - 1, j],
      [i, j + 1],
      [i, j - 1],
    ] as const)
      if (inMap(a, b) && !WATER_KINDS.has(kind[at(a, b)]!)) low = Math.min(low, level[at(a, b)]!);
    return low;
  };
  const seenPond = new Uint8Array(N);
  for (let n = 0; n < N; n++) {
    if (!pondish(kind[n]!) || seenPond[n]) continue;
    const list = [n];
    seenPond[n] = 1;
    for (let k = 0; k < list.length; k++) {
      const c = list[k]!;
      for (const q of [c + 1, c - 1, c + W, c - W])
        if (q >= 0 && q < N && Math.abs((q % W) - (c % W)) <= 1 && pondish(kind[q]!) && !seenPond[q]) {
          seenPond[q] = 1;
          list.push(q);
        }
    }
    const surface = Math.min(...list.map((q) => level[q]!), ...list.map((q) => bank(q) - 0.15));
    for (const q of list) level[q] = surface;
  }
  for (let n = 0; n < N; n++) if (kind[n] === K.brook) level[n] = Math.min(level[n]!, bank(n) - 0.15);

  // 6. The safe outline.
  const safe = new Uint8Array(N);
  for (let j = 0; j < H; j++)
    for (let i = 0; i < W; i++) if (inPolygon(spec.safe, i + 0.5, j + 0.5)) safe[at(i, j)] = 1;

  // Flights: tile levels at their centres (after grading, which never moves them).
  for (let n = 0; n < N; n++) if (flight[n]) level[n] = flightLevel(flights[flight[n]! - 1]!, (n % W) + 0.5, ((n / W) | 0) + 0.5);

  // Shallows (data/shallows.ts): the bank's edge of the water waded into, then swimming depth.
  for (const sh of SHALLOWS) {
    if (sh.layout !== spec.id) continue;
    const [x0, x1, y0, y1] = sh.rect;
    const wet = (i: number, j: number) => i >= 0 && j >= 0 && i < W && j < H && WATER_KINDS.has(kind[at(i, j)]!);
    for (let j = Math.max(0, y0); j < Math.min(H, y1); j++)
      for (let i = Math.max(0, x0); i < Math.min(W, x1); i++) {
        if (!wet(i, j)) continue;
        let d = 1;
        while (d < sh.swim && wet(i - d, j) && wet(i + d, j) && wet(i, j - d) && wet(i, j + d)) d++;
        if (d < sh.wade) deep[at(i, j)] = 1;
        else if (d < sh.swim) deep[at(i, j)] = 2;
      }
  }

  return { spec, w: W, h: H, kind, level, made, deep, flights, flight, structures, owner, safe, props: spec.props };
}


/**
 * Height of a flight at a point (metres): straight from `bottom` at the foot edge to `top` at the
 * head edge of its rectangle, or, for a path flight, by the distance along its path.
 */
export function flightLevel(f: MapFlight, x: number, y: number): number {
  if (f.path) {
    const [, s] = alongPath(f.path.pts, x, y);
    return lerp(f.path.from, f.path.to, s);
  }
  const [i0, i1, j0, j1] = f.rect;
  const t =
    f.down === 'e'
      ? (i1 - x) / (i1 - i0)
      : f.down === 'w'
        ? (x - i0) / (i1 - i0)
        : f.down === 's'
          ? (j1 - y) / (j1 - j0)
          : (y - j0) / (j1 - j0);
  return f.bottom + (f.top - f.bottom) * clamp(t, 0, 1);
}


// ── Turned footprints (lane KIT-EXT) ────────────────────────────────────────────────────────────

/** A map point in a building's unturned frame (map.ts `structureLocal`, the same turn). */
export function footLocal(b: LayoutBuilding, x: number, y: number): readonly [number, number] {
  if (!b.rot) return [x, y];
  const a = (b.rot * Math.PI) / 180;
  const [c, s] = [Math.cos(a), Math.sin(a)];
  const cx = (b.x0 + b.x1) / 2;
  const cy = (b.y0 + b.y1) / 2;
  const [dx, dy] = [x - cx, y - cy];
  return [cx + dx * c + dy * s, cy - dx * s + dy * c];
}

/** The tiles round a building's (turned) footprint: [x0, x1) × [y0, y1). */
export function footBounds(b: LayoutBuilding): Rect {
  if (!b.rot) return [b.x0, b.x1, b.y0, b.y1];
  const a = (b.rot * Math.PI) / 180;
  const [c, s] = [Math.cos(a), Math.sin(a)];
  const cx = (b.x0 + b.x1) / 2;
  const cy = (b.y0 + b.y1) / 2;
  const hx = (b.x1 - b.x0) / 2;
  const hy = (b.y1 - b.y0) / 2;
  const ex = Math.abs(hx * c) + Math.abs(hy * s);
  const ey = Math.abs(hx * s) + Math.abs(hy * c);
  return [Math.floor(cx - ex), Math.ceil(cx + ex), Math.floor(cy - ey), Math.ceil(cy + ey)];
}
